import { Box, ButtonBase, IconButton, keyframes, styled } from '@mui/material';
import { cornerBottom, orbRight } from 'src/helpers/floatingCorner';
import {
  AI_PILL_BG,
  AI_PILL_BG_DARK,
  AI_PILL_TEXT,
  AI_PILL_TEXT_DARK,
} from 'src/helpers/uiTokens';
import {
  CHAMPAGNE,
  GOLD,
  GOLD_GRADIENT,
  GOLD_INK,
  GOLD_RICH,
  GOLD_SOFT,
  GOLD_TEXT,
  GOLD_TEXT_GRADIENT,
  SHADOW_LG,
  SHADOW_MD,
} from './core.styles';

const EASE_OUT = 'cubic-bezier(.2,.9,.2,1)';

const popIn = keyframes`
  0% { opacity: 0; transform: scale(.4) translateY(24px); filter: blur(8px); }
  70% { opacity: 1; transform: scale(1.06) translateY(0); filter: blur(0); }
  100% { opacity: 1; transform: scale(1); }
`;
const ripple = keyframes`
  0% { transform: scale(.9); opacity: .45; }
  100% { transform: scale(1.6); opacity: 0; }
`;
const shimmer = keyframes`to { background-position: 200% center; }`;
const comet = keyframes`to { transform: rotate(360deg); }`;
const drift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(14%, -10%) scale(1.15); }
  66% { transform: translate(-12%, 8%) scale(.92); }
`;
const reveal = keyframes`
  from { clip-path: circle(30px at calc(100% - 34px) calc(100% - 34px)); opacity: .4; }
  to { clip-path: circle(160% at calc(100% - 34px) calc(100% - 34px)); opacity: 1; }
`;
const conceal = keyframes`
  from { clip-path: circle(160% at calc(100% - 34px) calc(100% - 34px)); opacity: 1; }
  to { clip-path: circle(30px at calc(100% - 34px) calc(100% - 34px)); opacity: 0; }
`;
const sheetUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const sheetDown = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;
/* Three bounces, each smaller: a hop, not a shake. */
const hop = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  14% { transform: translateY(-16px) scale(1.04); }
  28% { transform: translateY(0) scale(.97, 1.03); }
  42% { transform: translateY(-8px); }
  56% { transform: translateY(0); }
  68% { transform: translateY(-3px); }
  80% { transform: translateY(0); }
`;
const dotIn = keyframes`
  0% { transform: scale(0); }
  70% { transform: scale(1.25); }
  100% { transform: scale(1); }
`;
const dotPing = keyframes`
  0% { transform: scale(1); opacity: .55; }
  100% { transform: scale(2.4); opacity: 0; }
`;
const nudgeIn = keyframes`
  from { opacity: 0; transform: translateX(12px) scale(.96); }
  to { opacity: 1; transform: translateX(0) scale(1); }
`;

const ORB_SHADOW =
  '0 10px 24px -10px rgba(15, 39, 64, 0.6), 0 2px 6px -2px rgba(15, 39, 64, 0.35)';
const ORB_SHADOW_HOVER =
  '0 16px 32px -12px rgba(15, 39, 64, 0.7), 0 3px 8px -3px rgba(15, 39, 64, 0.4)';

export const shimmerText = {
  backgroundImage: `linear-gradient(90deg, currentColor 0%, ${GOLD_SOFT} 25%, ${GOLD_RICH} 50%, currentColor 75%)`,
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: `${shimmer} 2.4s linear infinite`,
};

/* ── the floating orb ─────────────────────────────────────────────────── */

// On desktop it shares the corner (floatingCorner): left of the employer
// support bubble when that is there, bottoms aligned, both raised over a
// page's own action bar.
export const OrbDock = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'lifted' && prop !== 'hidden',
})<{ lifted: boolean; hidden: boolean }>(({ theme, lifted, hidden }) => ({
  position: 'fixed',
  right: orbRight,
  bottom: cornerBottom,
  zIndex: theme.zIndex.speedDial,
  display: 'flex',
  alignItems: 'flex-end',
  gap: 12,
  pointerEvents: hidden ? 'none' : 'auto',
  opacity: hidden ? 0 : 1,
  transform: hidden ? 'scale(.6)' : 'none',
  // Sliding, too, when the support bubble comes or goes beside it.
  transition: `opacity .25s ease, transform .35s ${EASE_OUT}, right .3s ${EASE_OUT}, bottom .3s ${EASE_OUT}`,
  // The pop-in's fill would pin opacity at 1, so a hidden orb drops it.
  animation: hidden ? 'none' : `${popIn} .7s ${EASE_OUT} .4s backwards`,
  [theme.breakpoints.down('md')]: {
    right: 14,
    bottom: `calc(var(--bottom-nav-h, 0px) + ${lifted ? 76 : 14}px + env(safe-area-inset-bottom))`,
  },
}));

/* The core is the button: no surround, a soft navy shadow, and a faint gold
 * ripple as the one thing that asks to be noticed. When it has something to
 * say it hops once and wears a dot instead; the ripple rests meanwhile. */
export const OrbButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'hopping' && prop !== 'quiet',
})<{ hopping?: boolean; quiet?: boolean }>(({ theme, hopping, quiet }) => ({
  position: 'relative',
  width: 60,
  height: 60,
  borderRadius: '50%',
  boxShadow: ORB_SHADOW,
  transition: `transform .3s ${EASE_OUT}, box-shadow .3s ease`,
  animation: hopping ? `${hop} 1.2s ${EASE_OUT}` : 'none',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: `1.5px solid ${GOLD_SOFT}aa`,
    animation: quiet ? 'none' : `${ripple} 3.6s ease-out infinite`,
    opacity: quiet ? 0 : undefined,
  },
  '&::after': { animationDelay: '1.8s', borderColor: `${GOLD_RICH}77` },
  '&:hover, &:focus-visible': {
    transform: 'translateY(-3px) scale(1.06)',
    boxShadow: ORB_SHADOW_HOVER,
  },
  '&:active': { transform: 'scale(.94)' },
  [theme.breakpoints.down('md')]: { width: 54, height: 54 },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::before, &::after': { animation: 'none', opacity: 0 },
  },
}));

/* Something the orb said that hasn't been read: a dot that pings a few times
 * and then just stays. */
export const UnreadDot = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: 1,
  right: 1,
  width: 14,
  height: 14,
  borderRadius: '50%',
  background: theme.palette.error.main,
  border: `2.5px solid ${theme.palette.background.paper}`,
  animation: `${dotIn} .4s ${EASE_OUT} both`,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: -2.5,
    borderRadius: '50%',
    background: theme.palette.error.main,
    animation: `${dotPing} 1.6s ease-out 3`,
    opacity: 0,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::after': { animation: 'none' },
  },
}));

export const OrbLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'show',
})<{ show: boolean }>(({ theme, show }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 14px',
  borderRadius: 999,
  fontSize: 13.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: GOLD_INK,
  background: '#ffffff',
  border: `1px solid ${GOLD_SOFT}`,
  boxShadow: SHADOW_MD,
  backdropFilter: 'blur(12px)',
  opacity: show ? 1 : 0,
  transform: show ? 'translateX(0)' : 'translateX(14px)',
  transition: `opacity .25s ease, transform .35s ${EASE_OUT}`,
  pointerEvents: 'none',
  '& kbd': {
    fontFamily: 'inherit',
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 6,
    background: CHAMPAGNE,
    color: GOLD_TEXT,
  },
  [theme.breakpoints.down('md')]: { display: 'none' },
}));

/* What the orb says: tapping it acts on it. */
export const Nudge = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: 250,
  padding: '12px 32px 12px 14px',
  borderRadius: 16,
  fontSize: 13.5,
  lineHeight: 1.45,
  cursor: 'pointer',
  color: theme.palette.text.primary,
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: SHADOW_MD,
  animation: `${nudgeIn} .5s ${EASE_OUT} both`,
  transition: 'border-color .2s ease',
  '& b': { ...shimmerText, fontWeight: 700 },
  '& .go': {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: 6,
    fontSize: 12.5,
    fontWeight: 800,
    color: GOLD_TEXT,
    '& svg': { fontSize: 16, transition: `transform .25s ${EASE_OUT}` },
    ...theme.applyStyles('dark', { color: GOLD_SOFT }),
  },
  '&:hover': {
    borderColor: `${GOLD}88`,
    '& .go svg': { transform: 'translateX(3px)' },
  },
}));

/* ── the panel shell ─────────────────────────────────────────────────── */

export const PanelFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'closing' && prop !== 'wide',
})<{ closing: boolean; wide: boolean }>(({ theme, closing, wide }) => ({
  position: 'fixed',
  right: 20,
  bottom: 20,
  zIndex: theme.zIndex.modal + 1,
  width: wide ? 'min(720px, calc(100vw - 40px))' : 420,
  height: 'min(760px, calc(100dvh - 40px))',
  borderRadius: 28,
  padding: 1.5,
  // Clip, not hidden: the comet below overhangs every edge, and a hidden
  // frame counts that as room to scroll -- focusing the composer then slid
  // the whole panel up and bared the comet under it.
  overflow: 'hidden',
  '@supports (overflow: clip)': { overflow: 'clip' },
  isolation: 'isolate',
  boxShadow: SHADOW_LG,
  transition: `width .45s ${EASE_OUT}`,
  animation: `${closing ? conceal : reveal} ${closing ? '.28s' : '.55s'} ${EASE_OUT} both`,
  // The living border: a comet of light running round the rim.
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-60%',
    zIndex: -1,
    background: `conic-gradient(from 0deg, transparent 0deg, ${GOLD_SOFT}99 40deg, ${GOLD_RICH}88 80deg, transparent 140deg, transparent 220deg, ${GOLD}55 260deg, transparent 320deg)`,
    animation: `${comet} 7s linear infinite`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    borderRadius: 28,
    background: `${theme.palette.divider}`,
    opacity: 0.6,
  },
  [theme.breakpoints.down('sm')]: {
    inset: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100dvh',
    borderRadius: 0,
    padding: 0,
    animation: `${closing ? sheetDown : sheetUp} ${closing ? '.26s' : '.45s'} ${EASE_OUT} both`,
    '&::before, &::after': { display: 'none' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::before': { animation: 'none' },
  },
}));

export const PanelSurface = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: 26.5,
  overflow: 'hidden',
  color: theme.palette.text.primary,
  background: 'rgba(255, 253, 247, 0.95)',
  backdropFilter: 'blur(24px) saturate(160%)',
  ...theme.applyStyles('dark', {
    background: 'rgba(16, 14, 10, 0.95)',
  }),
  [theme.breakpoints.down('sm')]: { borderRadius: 0 },
}));

/* Slow aurora blobs and a fading dot grid behind the conversation. */
export const Aurora = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: '70%',
    aspectRatio: '1',
    borderRadius: '50%',
    filter: 'blur(60px)',
    opacity: 0.07,
    animation: `${drift} 18s ease-in-out infinite`,
    ...theme.applyStyles('dark', { opacity: 0.1 }),
  },
  '&::before': { top: '-25%', left: '-20%', background: GOLD_SOFT },
  '&::after': {
    bottom: '-30%',
    right: '-25%',
    background: GOLD_RICH,
    animationDelay: '-9s',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before, &::after': { animation: 'none' },
  },
}));

export const DotGrid = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
  backgroundImage: `radial-gradient(${theme.palette.text.primary}22 1px, transparent 1px)`,
  backgroundSize: '18px 18px',
  maskImage: 'linear-gradient(to bottom, #000 0%, transparent 38%)',
  WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, transparent 38%)',
  opacity: 0.6,
}));

export const PanelHeader = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 12px 12px 16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    paddingTop: 'calc(12px + env(safe-area-inset-top))',
  },
}));

export const HeaderTitle = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  '& h2': {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: '-.01em',
    backgroundImage: GOLD_TEXT_GRADIENT,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    width: 'fit-content',
    // Nested: in the dark theme applyStyles returns its styles as-is, so at the
    // top level this '& h2' would replace the rule above instead of adding to it.
    ...theme.applyStyles('dark', { backgroundImage: GOLD_GRADIENT }),
  },
}));

/* The AI-credit balance, in the same purple as every AI-credit price. */
export const CreditBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  flexShrink: 0,
  padding: '4px 9px 4px 6px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: AI_PILL_TEXT,
  background: AI_PILL_BG,
  '& img': { width: 15, height: 15 },
  ...theme.applyStyles('dark', {
    color: AI_PILL_TEXT_DARK,
    background: AI_PILL_BG_DARK,
  }),
}));

export const HeaderStatus = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'busy',
})<{ busy: boolean }>(({ theme, busy }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 2,
  fontSize: 12,
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  '& .dot': {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
    background: busy ? GOLD_RICH : '#22c55e',
    boxShadow: `0 0 0 3px ${busy ? GOLD_RICH : '#22c55e'}33`,
  },
  '& .label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...(busy ? shimmerText : {}),
  },
}));

export const HeaderButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.text.primary },
}));

export const ContextChip = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  alignSelf: 'flex-start',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  margin: '10px 16px 0',
  padding: '4px 10px 4px 8px',
  borderRadius: 999,
  fontSize: 11.5,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  background: `${GOLD}12`,
  border: `1px solid ${GOLD}2e`,
  '& svg': { fontSize: 14, color: GOLD_RICH },
}));

export const PanelBody = styled(Box)({
  position: 'relative',
  zIndex: 1,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '12px 16px 8px',
  scrollbarWidth: 'thin',
  maskImage: 'linear-gradient(to bottom, transparent 0, #000 14px)',
  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 14px)',
});
